import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import { ingestFile } from '../../orbit/lib/intakeApi';
import type { OrbitAsk, OrbitRoomFact } from '../../types/aai';
import { OrbitDeck } from '../../components/orbit-deck/OrbitDeck';
import type { DeckAnswer, DeckCardModel } from '../../components/orbit-deck/types';
import { transcribeFor } from '../orbit-deck/deckModel';
import { originLine } from './roomCopy';

/*
 * Filling one map: the same screen wherever Orbit still has questions.
 *
 * A map is what Orbit already holds plus what it still wants to know. What it
 * holds is collapsed by default, because the person in front of the screen is
 * here to answer, not to read back what they already told it. What is left is
 * dealt as cards, one at a time, each carrying why it is being asked, and each
 * answerable three ways: say it, drop a document, or skip it.
 *
 * The company frame, a department and a person are all this screen with a
 * different scope. Nothing about it knows which one it is drawing.
 */

const INPUT_OF: Record<OrbitAsk['kind'], DeckCardModel['input']> = {
  say: 'say',
  choose: 'choose',
  choose_many: 'choose_many',
  number: 'number',
  people: 'people',
};

export interface MapFillDone {
  title: string;
  /** What this map produced, one line each. */
  built: string[];
  action: string;
  onAction: () => void;
}

export const MapFill: React.FC<{
  clientId: string;
  title: string;
  /** Why these cards exist, in the words of this phase. */
  purpose: React.ReactNode;
  backLabel: string;
  onBack: () => void;
  held: OrbitRoomFact[];
  asks: OrbitAsk[];
  /** Saving an answer as the partner records them as the source. */
  isPartner: boolean;
  onChanged: () => void;
  done: MapFillDone;
  /** A panel beside the deck, for example Add context. */
  aside?: React.ReactNode;
  /** Fired once, when the person deals this map's last card. */
  onComplete?: () => void;
}> = ({ clientId, title, purpose, backLabel, onBack, held, asks, isPartner, onChanged, done, aside, onComplete }) => {
  const [finished, setFinished] = useState(false);
  /* Two decks: what is still to ask, and what Orbit already holds, each
     answered as cards. The held deck is where a guess is confirmed or put right. */
  const [tab, setTab] = useState<'ask' | 'held'>('ask');
  const [heldRound, setHeldRound] = useState(0);
  const [heldThrough, setHeldThrough] = useState(false);
  const [answered, setAnswered] = useState(0);
  const transcribe = useMemo(() => transcribeFor(clientId), [clientId]);

  const cards = useMemo<DeckCardModel[]>(() => asks.map((ask) => ({
    id: ask.id,
    tag: ask.origin === 'orbit_reading' ? 'confirm' : 'ask',
    tagLabel: originLine(ask),
    title: ask.question,
    why: ask.why,
    input: INPUT_OF[ask.kind] || 'say',
    options: ask.options,
    meta: { askId: ask.id },
  })), [asks]);

  const heldCards = useMemo<DeckCardModel[]>(() => held.map((f) => ({
    id: `held:${heldRound}:${f.field_key}`,
    tag: 'confirm',
    tagLabel: f.state === 'confirm' ? `To confirm · ${f.source || 'Orbit'}` : `Held · ${f.source || 'on file'}`,
    title: f.label,
    why: f.state === 'confirm' ? 'Orbit read this. Keep it if it is right, or put it right.' : 'Already on file. Change it if it has moved on.',
    input: 'confirm',
    facts: [{ field_key: f.field_key, label: f.label, value: f.value, value_display: f.value_display || 'nothing recorded',
              source: f.source, confidence: f.confidence }],
  })), [held, heldRound]);

  const confirmHeld = async (_card: DeckCardModel, value: DeckAnswer) => {
    const items = (value.confirm || []).map((d) => ({
      field_key: d.field_key, scope: d.scope || '', correct: d.action === 'correct', skip: d.action === 'skip', value: d.value,
    }));
    if (!items.length) return;
    try {
      await aaiApi.confirmOrbitFacts(items);
      onChanged();
    } catch {
      toast.error('Could not save that.');
      throw new Error('confirm failed');
    }
  };

  const answer = async (card: DeckCardModel, value: DeckAnswer) => {
    try {
      await aaiApi.answerOrbitAsk(card.id, value.value);
      setAnswered((n) => n + 1);
      onChanged();
    } catch {
      toast.error('Could not save that answer.');
      throw new Error('answer failed');
    }
  };

  const skip = async (card: DeckCardModel) => {
    try {
      await aaiApi.skipOrbitAsk(card.id);
      onChanged();
    } catch {
      toast.error('Could not keep that one for later.');
    }
  };

  // A document dropped on a card is read into the whole map, not just that
  // card: the file rarely answers only the question on screen.
  const document = async (card: DeckCardModel, file: File) => {
    try {
      const res = await ingestFile('doc', file, file.name, clientId);
      const facts = (res as unknown as { deltas?: unknown[] }).deltas?.length || 0;
      toast.success(`${file.name} read. ${facts} ${facts === 1 ? 'fact' : 'facts'} updated.`);
      onChanged();
    } catch {
      toast.error('Could not read that file.');
    }
    return undefined;
  };

  const heldCount = held.length;
  const askCount = asks.length;
  const complete = (finished || askCount === 0) && tab === 'ask';

  const tabs = (
    <div className="mb-4 flex w-max overflow-hidden rounded-full border border-line bg-white p-0.5" role="tablist" aria-label="Which cards">
      {([['held', 'Held', heldCount], ['ask', 'To ask', askCount]] as const).map(([id, word, n]) => (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={tab === id}
          onClick={() => { setTab(id); if (id === 'held') { setHeldThrough(false); setHeldRound((r) => r + 1); } }}
          className={`rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold transition-colors ${
            tab === id ? 'bg-ink text-white' : 'text-ink-muted hover:text-ink'
          }`}
        >
          <span className="tabular-nums">{n}</span> {word.toLowerCase()}
        </button>
      ))}
    </div>
  );

  // Only the deck emptying in front of someone counts as finishing the map. A
  // map that had no cards when they arrived is a map they have not worked, so
  // whatever the caller does with this is left to them to do by hand.
  const told = useRef(false);
  useEffect(() => {
    if (!finished || told.current) return;
    told.current = true;
    if (onComplete) onComplete();
  }, [finished, onComplete]);

  if (complete) {
    return (
      <>
        <div className="det-head">
          <button type="button" className="back" onClick={onBack}>← {backLabel}</button>
          <h1>{title}</h1>
        </div>
        <div className="fill-wrap">
          <p className="fill-purpose">{purpose}</p>
          {tabs}
          <div className="fill-done open">
            <p className="kick">Map complete</p>
            <h2>{done.title}</h2>
            <div className="bl">
              {done.built.map((line) => <div key={line} className="built-tick"><i>✓</i><span>{line}</span></div>)}
            </div>
            <button type="button" className="fill-go" onClick={done.onAction}>{done.action}</button>
          </div>
        </div>
      </>
    );
  }

  // The deck reads best narrow. A panel beside it needs the room to sit there
  // rather than under it, so the column widens only when one is passed.
  let wrapClass = 'fill-wrap';
  if (aside) wrapClass = 'fill-wrap wide';

  let label = 'Answer';
  let skipLabel = 'Skip, Orbit will ask again later';
  if (isPartner) {
    label = 'Answer for the client';
    skipLabel = 'Leave for the client';
  }

  return (
    <>
      <div className="det-head">
        <button type="button" className="back" onClick={onBack}>← {backLabel}</button>
        <h1>{title}</h1>
      </div>
      <div className={wrapClass}>
        <p className="fill-purpose">{purpose}</p>
        {/* Right under what these cards are for, each tab its own deck. */}
        {tabs}
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
          <div className="min-w-0 flex-1">
            {tab === 'ask' && (
              <OrbitDeck
                cards={cards}
                onAnswer={answer}
                onSkip={skip}
                onDocument={document}
                onDone={() => setFinished(true)}
                transcribe={transcribe}
                labels={{ deckTitle: 'To ask', primary: label, skip: skipLabel, skipWord: 'skip' }}
              />
            )}
            {tab === 'held' && heldCount > 0 && !heldThrough && (
              <OrbitDeck
                key={`held:${heldRound}`}
                cards={heldCards}
                onAnswer={confirmHeld}
                onSkip={() => undefined}
                onDocument={document}
                onDone={() => setHeldThrough(true)}
                transcribe={transcribe}
                labels={{ deckTitle: 'Held', deckNote: `${heldCount} Orbit already has`, primary: 'Save', skip: 'Leave it as it is', skipWord: 'next' }}
              />
            )}
            {tab === 'held' && (heldCount === 0 || heldThrough) && (
              <div className="rounded-[14px] border border-line bg-[var(--aa-card)] px-5 py-4 text-[13px] text-ink">
                {heldCount === 0 ? 'Orbit holds nothing here yet. Answer the cards to ask, or add context.' : 'That is everything Orbit holds here.'}
                {heldCount > 0 && (
                  <button type="button" className="ml-2 font-semibold underline" onClick={() => { setHeldRound((r) => r + 1); setHeldThrough(false); }}>
                    Go through them again
                  </button>
                )}
              </div>
            )}
            {answered > 0 && <p className="fill-note">{answered} answered in this session. Each one is filed with you as its source.</p>}
          </div>
          {aside && <div className="w-full shrink-0 fill-aside lg:w-[360px]">{aside}</div>}
        </div>
      </div>
    </>
  );
};

export default MapFill;
